import { Text } from "components/Typography/text.styled";
import { useTranslation } from "react-i18next";
import {
  AbsenceContainer,
  AbsenceDateContainer,
  AbsenceHoursContainer,
  AbsenceRow,
  AbsenceTypeContainer,
} from "./salary.styled";
import { FontFamily, FontSize } from "config/font";
import { Color } from "config/colors";
import { AbsenceType, AGIAbsence } from "model/Salary";
import { formatDuration } from "utils/utils";
import { useCallback } from "react";

interface AbsenceDetailRowProps {
  absences: AGIAbsence[];
}

export const AbsenceDetailRow: React.FC<AbsenceDetailRowProps> = (props) => {
  const { t } = useTranslation();

  const formatDate = useCallback((dateString: string): string => {
    try {
      return new Date(dateString).toISOString().split("T")[0];
    } catch (error) {
      console.error("Invalid date format:", dateString, error);
      return "Invalid date";
    }
  }, []);

  const translateAbsenceType = useCallback(
    (type: number): string => {
      switch (type) {
        case AbsenceType.PARENTAL_LEAVE:
          return t("Salary.parentalLeave");
        case AbsenceType.VAB:
          return t("Salary.VAB");
        default:
          return "-";
      }
    },
    [t]
  );

  const renderAbsenceRow = useCallback(
    (absence: AGIAbsence, index: number, totalLength: number) => {
      const isLastRow = index === totalLength - 1;

      return (
        <AbsenceRow
          lastRow={isLastRow}
          key={`absence-${absence.date}-${index}`}
        >
          <AbsenceDateContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Date")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {formatDate(absence.date)}
            </Text>
          </AbsenceDateContainer>
          <AbsenceTypeContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Type")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {translateAbsenceType(absence.type)}
            </Text>
          </AbsenceTypeContainer>
          <AbsenceHoursContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Hours")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {formatDuration(absence.hours.toString())}
            </Text>
          </AbsenceHoursContainer>
        </AbsenceRow>
      );
    },
    [t, formatDate, translateAbsenceType]
  );

  if (props.absences.length === 0) {
    return null;
  }

  return (
    <AbsenceContainer>
      <Text fontFamily={FontFamily.MontserratSemiBold}>
        {t("Salary.Absence")}
      </Text>
      {props.absences.map((absence, index) =>
        renderAbsenceRow(absence, index, props.absences.length)
      )}
    </AbsenceContainer>
  );
};
